import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useEffect } from "react";

import { Settings } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDeepChatSocketContext } from "../../../contexts/DeepChatSocketContext";
import { Feature } from "../../../featureFlags/featureFlagConfig";
import useFeatureFlag from "../../../featureFlags/useFeatureFlag";
import { BodyPanel, MainPanel } from "../Layout/Panels";
import useMarketResearchStore from "../useMarketResearchStore";
import QuestionInputBox from "../ViewStudy/QuestionInputBox";
import StudyTile from "./StudyTile";

export default function AllStudies() {
  const ChatAcrossStudiesFeature = useFeatureFlag(Feature.ChatAcrossStudies);
  const navigate = useNavigate();
  const DeepChatSocket = useDeepChatSocketContext();

  const {
    isLoadingStudies,
    studies,
    setSelectedStudyIds,
    setQuestion,
    question,
    askQuestion,
    toggleLoadingChat,
    isLoadingChat,
    resetSocket,
    clearChatSession,
    clearStudyFacetFilters,
    toggleFilterDrawer,
    setScope,
    filterCount,
  } = useMarketResearchStore();

  useEffect(() => {
    if (studies?.length) {
      setSelectedStudyIds();
    }

    if (isLoadingChat) {
      resetSocket();
    }

    setScope("list");
    // This can be switched to `true` after ChatAcrossStudies gets released to Prod
    toggleFilterDrawer(ChatAcrossStudiesFeature.enabled);

    clearStudyFacetFilters();
  }, []);

  const askDeepchatQuestion = (question: string) => {
    clearChatSession();
    toggleFilterDrawer(false);
    const selected_studies = (studies ?? []).map((_) => _.study_guid);
    setSelectedStudyIds(selected_studies);
    askQuestion(DeepChatSocket.sendJsonMessage, question, selected_studies);
    navigate(`/market-research/threads/`);
  };

  return (
    <MainPanel>
      <BodyPanel sx={{ minWidth: "325px" }}>
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
            <Typography variant="h5">All Studies</Typography>
            <Button
              onClick={() => {
                toggleFilterDrawer(false);
                navigate("/market-research/manage-studies");
              }}
              variant="text"
              color="primary"
              size="medium"
              startIcon={<Settings fontSize="small" sx={{ color: "#003087" }} />}
              sx={{
                color: "#003087",
                padding: "0.5em",
                minWidth: "unset",
                "& .MuiButton-startIcon": { marginRight: "unset" },
              }}
            ></Button>
          </Box>

          {ChatAcrossStudiesFeature.enabled && (
            <>
              <Box sx={{ my: "2em" }}>
                <QuestionInputBox
                  isDisabled={studies?.length === 0}
                  onQuestionInput={(text: string) => {
                    setQuestion(text);
                  }}
                  filterCount={filterCount()}
                  onFilterClick={() => toggleFilterDrawer()}
                  onSubmit={() => askDeepchatQuestion(question)}
                  question={question}
                  placeholder="Ask Deepsense..."
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: "1rem" }}>
                <Typography variant="h5" sx={{ fontSize: "20px" }}>
                  Explore an individual study:
                </Typography>
              </Box>
            </>
          )}

          {isLoadingStudies ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80%" }}>
              <CircularProgress />
            </Box>
          ) : studies?.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80%" }}>
              <Typography variant="h5" sx={{ mt: "5rem" }}>
                No studies found
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gap: "15px",
                gridTemplateColumns: {
                  xs: "repeat(1, minmax(0, 1fr))",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                  xl: "repeat(4, minmax(0, 1fr))",
                },
                alignItems: "start",
              }}
            >
              {(studies ?? []).map((study) => {
                return <StudyTile study={study} key={study.study_guid} />;
              })}
            </Box>
          )}
        </Box>
      </BodyPanel>
    </MainPanel>
  );
}
